import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Power, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const blank = () => ({
    code: "",
    discount_type: "percent",
    discount_value: 10,
    min_order_amount: "",
    max_discount_amount: "",
    usage_limit_total: "",
    usage_limit_per_phone: "",
    start_date: "",
    end_date: "",
    active: true,
    public: false,
    description: "",
});

function toPayload(form) {
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    return {
        code: (form.code || "").toUpperCase().replace(/\s+/g, ""),
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value || 0),
        min_order_amount: num(form.min_order_amount),
        max_discount_amount: num(form.max_discount_amount),
        usage_limit_total: form.usage_limit_total === "" ? null : parseInt(form.usage_limit_total, 10),
        usage_limit_per_phone: form.usage_limit_per_phone === "" ? null : parseInt(form.usage_limit_per_phone, 10),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        active: !!form.active,
        public: !!form.public,
        description: form.description || "",
    };
}

function toForm(d) {
    return {
        code: d.code || "",
        discount_type: d.discount_type || "percent",
        discount_value: d.discount_value ?? 0,
        min_order_amount: d.min_order_amount ?? "",
        max_discount_amount: d.max_discount_amount ?? "",
        usage_limit_total: d.usage_limit_total ?? "",
        usage_limit_per_phone: d.usage_limit_per_phone ?? "",
        start_date: d.start_date || "",
        end_date: d.end_date || "",
        active: d.active !== false,
        public: !!d.public,
        description: d.description || "",
    };
}

export default function AdminDiscounts() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // null=closed, "new" or id
    const [form, setForm] = useState(blank());
    const [saving, setSaving] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/discounts");
            setList(data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); }, []);

    const openNew = () => {
        setForm(blank());
        setEditing("new");
    };
    const openEdit = (d) => {
        setForm(toForm(d));
        setEditing(d.id);
    };
    const close = () => {
        setEditing(null);
        setForm(blank());
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = toPayload(form);
            if (!payload.code) {
                toast.error("Please enter a code");
                setSaving(false);
                return;
            }
            if (editing === "new") {
                await api.post("/admin/discounts", payload);
                toast.success(`Created ${payload.code}`);
            } else {
                await api.put(`/admin/discounts/${editing}`, payload);
                toast.success(`Updated ${payload.code}`);
            }
            close();
            reload();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
        }
        setSaving(false);
    };

    const toggleActive = async (d) => {
        try {
            const payload = toPayload(toForm({ ...d, active: !d.active }));
            await api.put(`/admin/discounts/${d.id}`, payload);
            reload();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const togglePublic = async (d) => {
        try {
            const payload = toPayload(toForm({ ...d, public: !d.public }));
            await api.put(`/admin/discounts/${d.id}`, payload);
            reload();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const remove = async (d) => {
        const usedMsg = d.usage_count > 0
            ? `Code ${d.code} has been used ${d.usage_count} time(s). It will be deactivated to preserve order history. Continue?`
            : `Delete code ${d.code}?`;
        if (!window.confirm(usedMsg)) return;
        try {
            const { data } = await api.delete(`/admin/discounts/${d.id}`);
            toast.success(data?.deactivated ? `Deactivated ${d.code}` : `Deleted ${d.code}`);
            reload();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-discounts-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-semibold">Marketing</div>
                    <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Discount Codes</h1>
                </div>
                <button
                    onClick={openNew}
                    className="inline-flex items-center gap-2 bg-[#00BCE5] text-black px-4 py-2.5 rounded-sm font-display font-semibold uppercase tracking-widest text-sm hover:bg-[#33C9EA]"
                    data-testid="discount-new-btn"
                >
                    <Plus size={14} /> New Code
                </button>
            </div>

            <div className="bg-[#18181B] border border-[#27272A] rounded-sm overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                    <thead className="bg-[#121214] text-xs uppercase tracking-widest font-display text-white/55">
                        <tr>
                            <th className="px-3 py-2 text-left">Code</th>
                            <th className="px-3 py-2 text-left">Type / Value</th>
                            <th className="px-3 py-2 text-left">Min order</th>
                            <th className="px-3 py-2 text-left">Used</th>
                            <th className="px-3 py-2 text-left">Public</th>
                            <th className="px-3 py-2 text-left">Active</th>
                            <th className="px-3 py-2 text-left">Window</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((d) => (
                            <tr key={d.id} className="border-t border-[#27272A]" data-testid={`discount-row-${d.code}`}>
                                <td className="px-3 py-2 font-display font-bold tracking-wider">{d.code}</td>
                                <td className="px-3 py-2 text-white/85">
                                    {d.discount_type === "percent"
                                        ? `${d.discount_value}% off${d.max_discount_amount ? ` (max Rs.${d.max_discount_amount})` : ""}`
                                        : `Rs.${d.discount_value} off`}
                                </td>
                                <td className="px-3 py-2 text-white/65">{d.min_order_amount ? `Rs.${d.min_order_amount}` : "—"}</td>
                                <td className="px-3 py-2 text-white/65">{d.usage_count || 0}{d.usage_limit_total ? ` / ${d.usage_limit_total}` : ""}</td>
                                <td className="px-3 py-2">
                                    <button onClick={() => togglePublic(d)} className={`text-xs uppercase tracking-widest font-display ${d.public ? "text-[#34C759]" : "text-white/45"}`} data-testid={`discount-public-${d.code}`}>
                                        {d.public ? <Eye size={14} /> : <EyeOff size={14} />}
                                    </button>
                                </td>
                                <td className="px-3 py-2">
                                    <button onClick={() => toggleActive(d)} className={`text-xs uppercase tracking-widest font-display ${d.active ? "text-[#34C759]" : "text-[#FF6E66]"}`} data-testid={`discount-active-${d.code}`}>
                                        <Power size={14} />
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-white/55 text-xs">
                                    {d.start_date || "—"} → {d.end_date || "—"}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => openEdit(d)} className="text-white/65 hover:text-[#00BCE5] mr-2" aria-label="Edit" data-testid={`discount-edit-${d.code}`}><Edit2 size={14} /></button>
                                    <button onClick={() => remove(d)} className="text-white/65 hover:text-[#FF6E66]" aria-label="Delete" data-testid={`discount-delete-${d.code}`}><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        {list.length === 0 && !loading && (
                            <tr><td colSpan={8} className="px-3 py-10 text-center text-white/55" data-testid="discounts-empty-state">No discount codes yet. Click "New Code" to create your first one.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <DiscountForm form={form} setForm={setForm} onSave={save} onClose={close} saving={saving} isNew={editing === "new"} />
            )}

            <style>{`
                .ost-input {
                    width: 100%;
                    background: #0A0A0B;
                    border: 1px solid #27272A;
                    border-radius: 4px;
                    padding: 10px 12px;
                    color: white;
                    outline: none;
                }
                .ost-input:focus { border-color: #00BCE5; }
            `}</style>
        </div>
    );
}

function DiscountForm({ form, setForm, onSave, onClose, saving, isNew }) {
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 p-3" role="dialog" data-testid="discount-form-modal">
            <div className="w-full max-w-2xl bg-[#0F0F13] border border-[#27272A] rounded-sm" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Discount</div>
                        <h2 className="font-display text-xl font-black uppercase tracking-tight">{isNew ? "New code" : `Edit ${form.code}`}</h2>
                    </div>
                    <button onClick={onClose} className="text-white/65 hover:text-white text-xl px-2">×</button>
                </div>
                <div className="p-4 grid md:grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto">
                    <Field label="Code (unique, no spaces)">
                        <input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase().replace(/\s+/g, ""))} className="ost-input" placeholder="WELCOME10" data-testid="discount-field-code" />
                    </Field>
                    <Field label="Type">
                        <select value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)} className="ost-input" data-testid="discount-field-type">
                            <option value="percent">Percentage</option>
                            <option value="fixed">Fixed amount</option>
                        </select>
                    </Field>
                    <Field label={form.discount_type === "percent" ? "Value (%)" : "Value (Rs.)"}>
                        <input type="number" min="0" value={form.discount_value} onChange={(e) => set("discount_value", e.target.value)} className="ost-input" data-testid="discount-field-value" />
                    </Field>
                    <Field label="Min order amount (Rs., optional)">
                        <input type="number" min="0" value={form.min_order_amount} onChange={(e) => set("min_order_amount", e.target.value)} className="ost-input" data-testid="discount-field-min" />
                    </Field>
                    {form.discount_type === "percent" && (
                        <Field label="Max discount (Rs., optional)">
                            <input type="number" min="0" value={form.max_discount_amount} onChange={(e) => set("max_discount_amount", e.target.value)} className="ost-input" data-testid="discount-field-max" />
                        </Field>
                    )}
                    <Field label="Total usage limit (optional)">
                        <input type="number" min="0" value={form.usage_limit_total} onChange={(e) => set("usage_limit_total", e.target.value)} className="ost-input" data-testid="discount-field-limit-total" />
                    </Field>
                    <Field label="Per-phone usage limit (optional)">
                        <input type="number" min="0" value={form.usage_limit_per_phone} onChange={(e) => set("usage_limit_per_phone", e.target.value)} className="ost-input" data-testid="discount-field-limit-phone" />
                    </Field>
                    <Field label="Start date (optional)">
                        <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="ost-input" data-testid="discount-field-start" />
                    </Field>
                    <Field label="End date (optional)">
                        <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="ost-input" data-testid="discount-field-end" />
                    </Field>
                    <div className="md:col-span-2">
                        <Field label="Description (shown to customers when public)">
                            <input value={form.description} onChange={(e) => set("description", e.target.value)} className="ost-input" placeholder="e.g. 10% off on your first order" data-testid="discount-field-description" />
                        </Field>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#00BCE5]" data-testid="discount-field-active" />
                        Active
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.public} onChange={(e) => set("public", e.target.checked)} className="accent-[#00BCE5]" data-testid="discount-field-public" />
                        Show as suggested code at checkout
                    </label>
                </div>
                <div className="p-4 border-t border-[#27272A] flex justify-end gap-2">
                    <button onClick={onClose} className="border border-[#27272A] hover:border-white/55 px-4 py-2 rounded-sm font-display uppercase tracking-widest text-sm text-white/85" data-testid="discount-form-cancel">Cancel</button>
                    <button onClick={onSave} disabled={saving} className="bg-[#00BCE5] text-black px-5 py-2 rounded-sm font-display font-bold uppercase tracking-widest text-sm hover:bg-[#33C9EA] disabled:opacity-50" data-testid="discount-form-save">
                        {saving ? "Saving…" : isNew ? "Create code" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1.5">{label}</label>
            {children}
        </div>
    );
}
